import React, { useState, useEffect, useRef } from "react";
import "react-toastify/dist/ReactToastify.css";
import SpeakerOnIcon from "./icons/SpeakerOnIcon";
import SpeakerOffIcon from "./icons/SpeakerOffIcon";
import { FastForwardDefault, FastForwardHover } from "./icons/FastForwardIcon";

function LMSStyle({
  char,
  handleStopTTS,
  displayedText,
  initialMessage,
  question,
  inputRef,
  handleInputChange,
  handleKeyDown,
  handleInputFocus,
  handleSendMessage,
  isLoading,
  isTyping,
  handleSkipTyping,
  messagesContainerRef,
  session_id,
  class_id,
  isTTSEnabled,
  setIsTTSEnabled,
  handleVoiceInput,
  isListening,
  speakHoverText,
  bc, // background color parameter
  tc, // text color parameter
}) {
  const [placeholderText, setPlaceholderText] = useState(
    "Ask me a question! 😊"
  );
  const placeholderOptions = [
    "Ask me a question! 😊",
    "Say something fun! 🤔",
    "What do you want to know? 💫",
    "Let's get started! 🚀",
    "I'm here to help! 😊",
  ];
  const [showStartMessage, setShowStartMessage] = useState(true);
  const [messageReceived, setMessageReceived] = useState(false);

  // Voice input helpers
  const listeningTimerRef = useRef(null);
  const listeningStartTimeRef = useRef(null);
  const lastTextChangeTimeRef = useRef(Date.now());
  const [autoVoiceSendEnabled, setAutoVoiceSendEnabled] = useState(false);
  const forceStopListeningRef = useRef(null);
  const originalHandleVoiceInputRef = useRef(handleVoiceInput);

  // Add a ref to track if TTS has been initialized - critical for Chrome
  const ttsInitializedRef = useRef(false);

  // Function to get colors based on character and session_id
  const getColors = () => {
    // If manual colors are provided, use them
    if (bc && tc) {
      return {
        backgroundColor: `#${bc}`,
        textColor: `#${tc}`,
      };
    }

    // Exception for specific session_id - keep default colors (only applies to LMSStyle)
    if (session_id === "4661e296af359628") {
      return {
        backgroundColor: "#73B9AF",
        textColor: "#000",
      };
    }

    // Character-based colors
    switch (char) {
      case "ren":
      case "ivy":
      case "felix":
        return {
          backgroundColor: "#F4B940",
          textColor: "#000000",
        };
      case "alex":
        return {
          backgroundColor: "#6BAFAF",
          textColor: "#000",
        };
      case "gold":
        return {
          backgroundColor: "#065BA5",
          textColor: "#000000",
        };
      case "kel":
        return {
          backgroundColor: "#007CA9",
          textColor: "#000000",
        };
      default:
        // Default colors for other characters and "test style"
        return {
          backgroundColor: "#73B9AF",
          textColor: "#000",
        };
    }
  };

  const colors = getColors();

  // Convert hex to rgba for semi-transparent backgrounds
  const hexToRgba = (hex, alpha = 1) => {
    if (!hex) return `rgba(0,0,0,${alpha})`;
    let h = hex.replace("#", "");
    if (h.length === 3)
      h = h
        .split("")
        .map((c) => c + c)
        .join("");
    const num = parseInt(h, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  const chatBg = hexToRgba(colors.backgroundColor, 0.2);

  // Determine if voice input should be allowed
  // Allow voice input immediately as in original implementation
  const shouldAllowVoiceInput = !isLoading && !(isTyping && messageReceived);

  // Preload TTS on component mount - this is critical for Chrome
  useEffect(() => {
    // Initialize TTS with a silent utterance on page load
    const preloadTTS = () => {
      if (!ttsInitializedRef.current && window.speechSynthesis) {
        // Create an empty utterance to initialize the speech engine
        const utterance = new SpeechSynthesisUtterance("");
        utterance.volume = 0; // Make it silent
        utterance.rate = 0.85;

        // Initialize the engine
        window.speechSynthesis.speak(utterance);

        // Cancel it immediately (we just need to initialize)
        window.speechSynthesis.cancel();

        ttsInitializedRef.current = true;
        console.log("TTS engine preloaded");
      }
    };

    // Try to preload after a small delay to ensure the page is fully loaded
    setTimeout(preloadTTS, 500);

    // Also try during user interaction (scroll, mousemove) before they click start
    const handleUserInteraction = () => {
      if (!ttsInitializedRef.current) {
        preloadTTS();
      }
    };

    // Add event listeners for common user interactions
    document.addEventListener("scroll", handleUserInteraction, {
      passive: true,
      once: true,
    });
    document.addEventListener("mousemove", handleUserInteraction, {
      passive: true,
      once: true,
    });

    return () => {
      document.removeEventListener("scroll", handleUserInteraction);
      document.removeEventListener("mousemove", handleUserInteraction);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderText((prev) => {
        const currentIndex = placeholderOptions.indexOf(prev);
        const nextIndex = (currentIndex + 1) % placeholderOptions.length;
        return placeholderOptions[nextIndex];
      });
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  // Simple welcome message implementation without blocking interaction
  useEffect(() => {
    if (displayedText) {
      setShowStartMessage(false);
      setMessageReceived(true);
    }
  }, [displayedText]);

  // Scroll to bottom when a new message appears
  useEffect(() => {
    if (messagesContainerRef?.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  }, [displayedText]);

  // Keep latest voice-input handler
  useEffect(() => {
    originalHandleVoiceInputRef.current = handleVoiceInput;
  }, [handleVoiceInput]);

  // Listen for question changes and update the lastTextChangeTime
  useEffect(() => {
    if (isListening) {
      lastTextChangeTimeRef.current = Date.now();
    }
  }, [question, isListening]);

  // This effect actively stops listening when conditions change
  useEffect(() => {
    if (isListening && !shouldAllowVoiceInput) {
      if (forceStopListeningRef.current) {
        forceStopListeningRef.current();
      }
    }
  }, [isListening, shouldAllowVoiceInput, isTyping, isLoading, displayedText]);

  // Voice input auto-send functionality
  useEffect(() => {
    if (isListening && shouldAllowVoiceInput) {
      if (!listeningStartTimeRef.current) {
        listeningStartTimeRef.current = Date.now();
        lastTextChangeTimeRef.current = Date.now();
      }

      listeningTimerRef.current = setInterval(() => {
        const timeSinceLastTextChange =
          Date.now() - lastTextChangeTimeRef.current;

        if (timeSinceLastTextChange >= 4000 && question.trim() !== "") {
          setAutoVoiceSendEnabled(true);
          handleInputSendMessage();
          clearInterval(listeningTimerRef.current);
        }
      }, 300);
    } else {
      if (listeningTimerRef.current) {
        clearInterval(listeningTimerRef.current);
        listeningTimerRef.current = null;
      }
      listeningStartTimeRef.current = null;
    }

    // Auto-restart listening after typing completes if auto voice was enabled
    if (autoVoiceSendEnabled && shouldAllowVoiceInput && !isListening) {
      setAutoVoiceSendEnabled(false);
      setTimeout(() => {
        if (shouldAllowVoiceInput) {
          originalHandleVoiceInputRef.current();
        }
      }, 500);
    }

    return () => {
      if (listeningTimerRef.current) {
        clearInterval(listeningTimerRef.current);
      }
    };
  }, [
    isListening,
    question,
    isLoading,
    isTyping,
    autoVoiceSendEnabled,
    displayedText,
    shouldAllowVoiceInput,
  ]);

  // Long inactivity timeout for mic
  useEffect(() => {
    if (isListening) {
      const inactivityTimeout = setTimeout(() => {
        if (forceStopListeningRef.current) {
          forceStopListeningRef.current();
        }
      }, 90000); // 90 seconds
      return () => clearTimeout(inactivityTimeout);
    }
  }, [question, isListening]);

  // Preload videos for current character
  useEffect(() => {
    const m = mediaMap[(char || "").toLowerCase()] || mediaMap.default;
    const vids = [m.typing, m.idle].map((src) => {
      const v = document.createElement("video");
      v.preload = "auto";
      v.src = src;
      return v;
    });
    return () => vids.forEach((v) => (v.src = ""));
  }, [char]);

  // Updated TTS button to also stop TTS and skip typing
  const handleTTSButtonClick = () => {
    handleStopTTS();
    handleSkipTyping();
    setIsTTSEnabled((prev) => !prev);
  };

  // Handle fast-forward button (skip TTS and typing)
  const handleFastForward = () => {
    handleStopTTS();
    handleSkipTyping();
  };

  const handleInputSendMessage = () => {
    handleSendMessage();
    if (isTTSEnabled) console.log("TTS Start: ", question);
    setMessageReceived(false);
  };

  // Custom voice input handler that only activates when allowed
  const handleSafeVoiceInput = () => {
    if (shouldAllowVoiceInput) {
      handleVoiceInput();
    }
  };

  // Function to forcibly stop listening
  const forceStopVoiceInput = () => {
    if (isListening) {
      handleVoiceInput();
    }
  };

  // Store the force stop function in the ref so effects can access it
  useEffect(() => {
    forceStopListeningRef.current = forceStopVoiceInput;
  }, [isListening]);

  // Create a wrapped handler that only calls the original onChange handler when not typing
  const handleWrappedInputChange = (e) => {
    if (!isTyping) {
      handleInputChange(e);
    }
  };

  // Form submit handler
  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (isLoading) return;

    if (isTyping && messageReceived) {
      handleFastForward();
      return;
    }

    if (!question?.trim()) return;
    handleInputSendMessage();
  };

  const canFastForward = isTyping && messageReceived;

  const media = (name) => `/media/${encodeURI(name)}`;

  // avatar video mapping (same as LMSActivityStyle)
  const mediaMap = {
    felix: {
      typing: media("FelixTalk.mp4"),
      idle: media("FelixIdle.mp4"),
    },
    ivy: {
      typing: media("IvyTalk.mp4"),
      idle: media("IvyIdle.mp4"),
    },
    ren: {
      typing: media("RenTalk.mp4"),
      idle: media("RenIdle.mp4"),
    },
    kel: {
      typing: media("KelvinTalk.mp4"),
      idle: media("KelvinIdle.mp4"),
    },
    gold: {
      typing: media("GoldenTalk.mp4"),
      idle: media("GoldenIdle.mp4"),
    },
    alex: {
      typing: media("AlexTalk.mp4"),
      idle: media("AlexIdle.mp4"),
    },
    default: {
      typing: media("FelixTalk.mp4"),
      idle: media("FelixIdle.mp4"),
    },
  };

  const chosenChar = mediaMap[(char || "").toLowerCase()] || mediaMap.default;
  const mediaSrc = isTyping ? chosenChar.typing : chosenChar.idle;

  // Figma input pill styles (same as LMSActivityStyle)
  const figmaInputStyles = `
    /* Disclaimer under the input pill — Figma spec */
    .input-disclaimer {
      margin: 8px auto 0; /* 8px gap under pill, centered */
      width: 100%;
      max-width: 470px; /* matches Figma width, stays responsive */
      height: 17px; /* optional: mirrors Figma frame height */
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      font-weight: 400; /* Regular */
      font-size: 14px;
      line-height: 14px; /* 100% line-height */
      letter-spacing: 0;
      text-align: center;
      color: #525252;
      user-select: none;
      white-space: normal;
    }
    @supports (padding: max(0px)) {
      .input-disclaimer {
        padding-bottom: max(0px, env(safe-area-inset-bottom));
      }
    }

    /* Fixed rail that won't overflow on the right even with scrollbars */
    .input-fixed-rail {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 32px; /* same as before */
      padding-inline: 32px; /* matches chat area's p-8 */
      box-sizing: border-box; /* padding is inside the 100% width */
      width: 100%;
    }

    .input-section {
      padding: 16px 8px 20px;
      background: #FFFFFF;
      border-top: none;
    }

    .input-form {
      width: 100%;
    }

    .input-wrapper.figma-pill {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 24px;
      padding: 8px;
      width: 100%;
      max-width: 100%;
      height: 72px;
      margin: 0;
      background: #ECECEE;
      border: 2px solid #D9D9DE;
      border-radius: 999px;
      box-sizing: border-box;
    }

    .chat-input.figma {
      flex: 1;
      min-width: 0;
      height: 56px;
      border: none;
      background: transparent;
      padding: 0 4px;
      outline: none;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      font-style: normal;
      font-weight: 400;
      font-size: 16px;
      line-height: 19px;
      color: #212121;
    }

    .chat-input.figma::placeholder {
      color: #525252;
    }

    .mic-btn {
      position: relative;
      width: 56px;
      height: 56px;
      padding: 0;
      border: none;
      background: transparent;
      border-radius: 999px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: opacity 0.2s ease;
    }

    .mic-btn.is-disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .mic-btn .icon {
      position: absolute;
      inset: 0;
      display: none;
      align-items: center;
      justify-content: center;
      line-height: 0;
    }

    .mic-btn.is-muted .icon-muted {
      display: flex;
    }

    .mic-btn.is-listening .icon-listening {
      display: flex;
    }

    .mic-btn.is-listening:hover:not(.is-disabled) .icon-listening {
      display: none;
    }

    .mic-btn.is-listening:hover:not(.is-disabled) .icon-listening-hover {
      display: flex;
    }

    .mic-btn.is-listening:not(.is-disabled) {
      animation: micPulse 2s infinite ease-in-out;
    }

    @keyframes micPulse {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.7;
      }
    }

    .send-btn {
      position: relative;
      width: 56px;
      height: 56px;
      padding: 0;
      border: none;
      border-radius: 999px;
      background: transparent;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      transition: transform 0.15s ease;
    }

    .send-btn:disabled {
      cursor: not-allowed;
    }

    .send-btn .icon {
      position: absolute;
      inset: 0;
      display: none;
      align-items: center;
      justify-content: center;
      line-height: 0;
    }

    .send-btn .icon.default {
      display: flex;
    }

    .send-btn:hover:not(:disabled):not(.is-loading) .icon.default {
      display: none;
    }

    .send-btn:hover:not(:disabled):not(.is-loading) .icon.hover {
      display: flex;
    }

    .send-btn:disabled .icon {
      display: none;
    }

    .send-btn:disabled .icon.disabled {
      display: flex;
    }

    .send-btn.is-loading .icon {
      display: none;
    }

    .send-btn.is-loading .icon.loading {
      display: flex;
    }

    .send-btn .loading-bg {
      width: 56px;
      height: 56px;
      border-radius: 999px;
      background: #212121;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .send-btn .loading-bg .dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #FFFFFF;
      margin: 0 2px;
      animation: sendDots 1.1s infinite ease-in-out;
    }

    .send-btn .loading-bg .dot:nth-child(2) {
      animation-delay: 0.15s;
    }

    .send-btn .loading-bg .dot:nth-child(3) {
      animation-delay: 0.3s;
    }

    @keyframes sendDots {
      0%, 80%, 100% {
        opacity: 0.45;
        transform: translateY(0);
      }
      40% {
        opacity: 1;
        transform: translateY(-2px);
      }
    }

    .send-btn svg,
    .mic-btn svg {
      display: block;
      pointer-events: none;
    }

    /* Avatar video fit: default avoid zoom on wide; crop vertically on very tall */
    .avatar-video {
      object-fit: contain;
    }

    @media (max-aspect-ratio: 3/4) {
      .avatar-video {
        object-fit: cover;
      }
    }

    /* Avatar pane: lock to 50% of the *viewport* height, with modern fallbacks */
    .avatar-pane {
      height: 50vh; 
    } /* fallback */

    @supports (height: 1svh) {
      .avatar-pane {
        height: 50svh;
      }
    } /* iOS stable vh */

    @supports (height: 1dvh) {
      .avatar-pane {
        height: 50dvh;
      }
    } /* dynamic vh */
  `;

  return (
    <div
      className="fixed inset-0 flex flex-col"
      style={{ backgroundColor: colors.backgroundColor, margin: 0, padding: 0 }}
    >
      <style>{figmaInputStyles}</style>

      {/* Top Bar for Icons */}
      <div className="flex items-center justify-between p-4 relative z-10">
        <div className="flex items-center gap-8">
          {/* Dynamic TTS Button */}
          <button
            onClick={handleTTSButtonClick}
            className="p-0 transition duration-300"
            aria-label={isTTSEnabled ? "Mute TTS" : "Unmute TTS"}
          >
            {isTTSEnabled ? <SpeakerOnIcon /> : <SpeakerOffIcon />}
          </button>
        </div>
      </div>

      {/* Avatar Section - Using same styling as LMSActivityStyle */}
      <div
        className="absolute top-0 left-0 right-0 w-full flex flex-col overflow-hidden avatar-pane"
        style={{ backgroundColor: colors.backgroundColor }}
      >
        <div className="absolute inset-0">
          <video
            key={isTyping ? "typing" : "idle"}
            src={mediaSrc}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="avatar-video"
            style={{
              width: "100%",
              height: "100%",
              display: "block",
              objectPosition: "bottom center",
            }}
          />
        </div>
      </div>

      {/* Bottom Section: Chat Area and Input */}
      <div
        className="absolute bottom-0 left-0 right-0 w-full flex flex-col items-center justify-start p-8 z-10"
        style={{ backgroundColor: "#FFFFFF", height: "50%" }}
      >
        <div
          ref={messagesContainerRef}
          style={{
            maxHeight: "calc(100% - 150px)",
            overflowY: "auto",
            width: "100%",
            padding: "8px",
            flexGrow: 1,
          }}
        >
          {/* Displayed Text */}
          {displayedText && (
            <div
              className="p-4 text-lg md:text-xl rounded-lg shadow-md break-words cursor-pointer"
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: "20px",
                fontWeight: "500",
                lineHeight: "normal",
                color: colors.textColor,
                textAlign: "center",
                backgroundColor: chatBg,
                backdropFilter: "blur(30px)",
                marginBottom: "16px",
                width: "100%",
              }}
              onMouseEnter={() => {
                if (!isTyping && speakHoverText) {
                  speakHoverText(displayedText);
                }
              }}
              onClick={handleSkipTyping}
            >
              <span>{displayedText}</span>
            </div>
          )}

          {/* Start Message (No start button, just message) */}
          {showStartMessage && (
            <div
              className="p-4 text-lg md:text-xl rounded-lg shadow-md mb-4 w-full text-center"
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: "20px",
                fontWeight: "500",
                lineHeight: "normal",
                color: colors.textColor,
                backgroundColor: chatBg,
                backdropFilter: "blur(30px)",
              }}
            >
              Welcome! Whenever you're ready, let's dive into your questions and
              start exploring.
            </div>
          )}
        </div>

        {/* Input Section - Using same styling as LMSActivityStyle */}
        <div className="w-full input-fixed-rail">
          <div className="input-section">
            <form className="input-form" onSubmit={handleSubmit}>
              <div className="input-wrapper figma-pill">
                {/* Mic button - 56x56 */}
                <button
                  type="button"
                  className={`mic-btn ${
                    isListening ? "is-listening" : "is-muted"
                  } ${!shouldAllowVoiceInput ? "is-disabled" : ""}`}
                  aria-label={
                    isListening ? "Stop listening" : "Start voice input"
                  }
                  aria-pressed={isListening}
                  onClick={handleSafeVoiceInput}
                  disabled={!shouldAllowVoiceInput}
                >
                  {/* Default (muted) */}
                  <span className="icon icon-muted" aria-hidden="true">
                    <svg
                      width="56"
                      height="56"
                      viewBox="0 0 56 56"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="1"
                        y="1"
                        width="54"
                        height="54"
                        rx="27"
                        stroke="#D9D9DE"
                        strokeWidth="2"
                      />
                      <g clipPath="url(#clip0_muted)">
                        <path
                          d="M15.9219 14.8328C15.4812 14.3922 14.7687 14.3922 14.3328 14.8328C13.8969 15.2734 13.8922 15.9859 14.3281 16.4265L39.0781 41.1765C39.5188 41.6172 40.2313 41.6172 40.6672 41.1765C41.1031 40.7359 41.1078 40.0234 40.6672 39.5875L33.9078 32.8187C35.5109 31.1968 36.5 28.9656 36.5 26.5V24.625C36.5 24.0015 35.9984 23.5 35.375 23.5C34.7516 23.5 34.25 24.0015 34.25 24.625V26.5C34.25 28.3422 33.5141 30.0109 32.3141 31.2297L30.725 29.6406C31.5125 28.8297 32 27.7187 32 26.5V20.5C32 18.0156 29.9844 16 27.5 16C25.0156 16 23 18.0156 23 20.5V21.9156L15.9219 14.8375V14.8328ZM29.9047 35.1765L27.9641 33.2359C27.8094 33.2453 27.6594 33.25 27.5047 33.25C23.7781 33.25 20.7547 30.2265 20.7547 26.5V26.0219L18.7109 23.9781C18.5797 24.1609 18.5047 24.3859 18.5047 24.625V26.5C18.5047 31.089 21.9406 34.8765 26.3797 35.4297V37.75H24.1297C23.5062 37.75 23.0047 38.2515 23.0047 38.875C23.0047 39.4984 23.5062 40 24.1297 40H30.8797C31.5031 40 32.0047 39.4984 32.0047 38.875C32.0047 38.2515 31.5031 37.75 30.8797 37.75H28.6297V35.4297C29.0656 35.3734 29.4922 35.289 29.9094 35.1765H29.9047Z"
                          fill="#212121"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_muted">
                          <rect
                            width="24"
                            height="24"
                            fill="white"
                            transform="translate(16 16)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                  </span>

                  {/* Listening (selected) */}
                  <span className="icon icon-listening" aria-hidden="true">
                    <svg
                      width="56"
                      height="56"
                      viewBox="0 0 56 56"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="56" height="56" rx="28" fill="#212121" />
                      <path
                        d="M28 16C25.5156 16 23.5 18.0156 23.5 20.5V26.5C23.5 28.9844 25.5156 31 28 31C30.4844 31 32.5 28.9844 32.5 26.5V20.5C32.5 18.0156 30.4844 16 28 16ZM21.25 24.625C21.25 24.0016 20.7484 23.5 20.125 23.5C19.5016 23.5 19 24.0016 19 24.625V26.5C19 31.0891 22.4359 34.8766 26.875 35.4297V37.75H24.625C24.0016 37.75 23.5 38.2516 23.5 38.875C23.5 39.4984 24.0016 40 24.625 40H31.375C31.9984 40 32.5 39.4984 32.5 38.875C32.5 38.2516 31.9984 37.75 31.375 37.75H29.125V35.4297C33.5641 34.8766 37 31.0891 37 26.5V24.625C37 24.0016 36.4984 23.5 35.875 23.5C35.2516 23.5 34.75 24.0016 34.75 24.625V26.5C34.75 30.2266 31.7266 33.25 28 33.25C24.2734 33.25 21.25 30.2266 21.25 26.5V24.625Z"
                        fill="white"
                      />
                    </svg>
                  </span>

                  {/* Listening hover → show "mute" preview */}
                  <span
                    className="icon icon-listening-hover"
                    aria-hidden="true"
                  >
                    <svg
                      width="56"
                      height="56"
                      viewBox="0 0 56 56"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="56" height="56" rx="28" fill="#212121" />
                      <g clipPath="url(#clip0_hover)">
                        <path
                          d="M15.9219 14.8328C15.4812 14.3922 14.7687 14.3922 14.3328 14.8328C13.8969 15.2734 13.8922 15.9859 14.3281 16.4265L39.0781 41.1765C39.5188 41.6172 40.2313 41.6172 40.6672 41.1765C41.1031 40.7359 41.1078 40.0234 40.6672 39.5875L33.9078 32.8187C35.5109 31.1968 36.5 28.9656 36.5 26.5V24.625C36.5 24.0015 35.9984 23.5 35.375 23.5C34.7516 23.5 34.25 24.0015 34.25 24.625V26.5C34.25 28.3422 33.5141 30.0109 32.3141 31.2297L30.725 29.6406C31.5125 28.8297 32 27.7187 32 26.5V20.5C32 18.0156 29.9844 16 27.5 16C25.0156 16 23 18.0156 23 20.5V21.9156L15.9219 14.8375V14.8328ZM29.9047 35.1765L27.9641 33.2359C27.8094 33.2453 27.6594 33.25 27.5047 33.25C23.7781 33.25 20.7547 30.2265 20.7547 26.5V26.0219L18.7109 23.9781C18.5797 24.1609 18.5047 24.3859 18.5047 24.625V26.5C18.5047 31.089 21.9406 34.8765 26.3797 35.4297V37.75H24.1297C23.5062 37.75 23.0047 38.2515 23.0047 38.875C23.0047 39.4984 23.5062 40 24.1297 40H30.8797C31.5031 40 32.0047 39.4984 32.0047 38.875C32.0047 38.2515 31.5031 37.75 30.8797 37.75H28.6297V35.4297C29.0656 35.3734 29.4922 35.289 29.9094 35.1765H29.9047Z"
                          fill="white"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_hover">
                          <rect
                            width="24"
                            height="24"
                            fill="white"
                            transform="translate(16 16)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                  </span>
                </button>

                <input
                  ref={inputRef}
                  type="text"
                  className="chat-input figma"
                  value={question || ""}
                  onChange={handleWrappedInputChange}
                  onKeyDown={handleKeyDown}
                  onFocus={handleInputFocus}
                  placeholder={placeholderText}
                  disabled={!!isLoading}
                  readOnly={isTyping}
                  autoComplete="off"
                />

                <button
                  type="submit"
                  className={`send-btn ${isLoading ? "is-loading" : ""}`}
                  aria-label={canFastForward ? "Skip Typing" : "Send"}
                  aria-busy={!!isLoading}
                  disabled={
                    isLoading ||
                    (isTyping && !messageReceived) ||
                    (!question?.trim() && !isTyping)
                  }
                >
                  {/* Default / Hover switch based on fast-forward availability */}
                  {canFastForward ? (
                    <>
                      <span className="icon default" aria-hidden="true">
                        <FastForwardDefault />
                      </span>
                      <span className="icon hover" aria-hidden="true">
                        <FastForwardHover />
                      </span>
                    </>
                  ) : (
                    <>
                      {/* Send default svg */}
                      <span className="icon default" aria-hidden="true">
                        <svg
                          width="56"
                          height="56"
                          viewBox="0 0 56 56"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect
                            x="1"
                            y="1"
                            width="54"
                            height="54"
                            rx="27"
                            stroke="#D9D9DE"
                            strokeWidth="2"
                          />
                          <path
                            d="M18.0515 16.1406L39.0382 26.4062C39.625 26.6922 40 27.3156 40 28C40 28.6844 39.625 29.3078 39.0382 29.5938L18.0515 39.8594C17.8618 39.9531 17.6544 40 17.4471 40C16.6485 40 16 39.3062 16 38.4531C16 38.2375 16.0441 38.0219 16.1235 37.825L19.3 30.2359C19.5074 29.7391 19.9574 29.3969 20.4691 29.35L27.3824 28.7125C27.7309 28.6797 27.9956 28.3703 27.9956 28C27.9956 27.6297 27.7265 27.3203 27.3824 27.2875L20.4691 26.65C19.9574 26.6031 19.5074 26.2656 19.3 25.7641L16.1235 18.175C16.0441 17.9781 16 17.7625 16 17.5469C16 16.6937 16.6485 16 17.4471 16C17.6544 16 17.8618 16.0469 18.0515 16.1406Z"
                            fill="#212121"
                          />
                        </svg>
                      </span>
                      {/* Send hover svg */}
                      <span className="icon hover" aria-hidden="true">
                        <svg
                          width="56"
                          height="56"
                          viewBox="0 0 56 56"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect width="56" height="56" rx="28" fill="#212121" />
                          <path
                            d="M18.0515 16.1406L39.0382 26.4062C39.625 26.6922 40 27.3156 40 28C40 28.6844 39.625 29.3078 39.0382 29.5938L18.0515 39.8594C17.8618 39.9531 17.6544 40 17.4471 40C16.6485 40 16 39.3062 16 38.4531C16 38.2375 16.0441 38.0219 16.1235 37.825L19.3 30.2359C19.5074 29.7391 19.9574 29.3969 20.4691 29.35L27.3824 28.7125C27.7309 28.6797 27.9956 28.3703 27.9956 28C27.9956 27.6297 27.7265 27.3203 27.3824 27.2875L20.4691 26.65C19.9574 26.6031 19.5074 26.2656 19.3 25.7641L16.1235 18.175C16.0441 17.9781 16 17.7625 16 17.5469C16 16.6937 16.6485 16 17.4471 16C17.6544 16 17.8618 16.0469 18.0515 16.1406Z"
                            fill="white"
                          />
                        </svg>
                      </span>
                    </>
                  )}

                  {/* Disabled */}
                  <span className="icon disabled" aria-hidden="true">
                    <svg
                      width="56"
                      height="56"
                      viewBox="0 0 56 56"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="1"
                        y="1"
                        width="54"
                        height="54"
                        rx="27"
                        stroke="#D9D9DE"
                        strokeWidth="2"
                      />
                      <path
                        d="M18.0515 16.1406L39.0382 26.4062C39.625 26.6922 40 27.3156 40 28C40 28.6844 39.625 29.3078 39.0382 29.5938L18.0515 39.8594C17.8618 39.9531 17.6544 40 17.4471 40C16.6485 40 16 39.3062 16 38.4531C16 38.2375 16.0441 38.0219 16.1235 37.825L19.3 30.2359C19.5074 29.7391 19.9574 29.3969 20.4691 29.35L27.3824 28.7125C27.7309 28.6797 27.9956 28.3703 27.9956 28C27.9956 27.6297 27.7265 27.3203 27.3824 27.2875L20.4691 26.65C19.9574 26.6031 19.5074 26.2656 19.3 25.7641L16.1235 18.175C16.0441 17.9781 16 17.7625 16 17.5469C16 16.6937 16.6485 16 17.4471 16C17.6544 16 17.8618 16.0469 18.0515 16.1406Z"
                        fill="#D9D9DE"
                      />
                    </svg>
                  </span>

                  {/* Loading (three dots) */}
                  <span className="icon loading" aria-hidden="true">
                    <span className="loading-bg">
                      <span className="dot" />
                      <span className="dot" />
                      <span className="dot" />
                    </span>
                  </span>
                </button>
              </div>
            </form>
            <p className="input-disclaimer" role="note">
              AI responses are for general guidance only. Please verify before
              acting.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LMSStyle;
